import { buildRelationshipHierarchy, getKeysInForm } from "@hightouch/formkit";
import type { FormkitNode } from "@hightouch/formkit";
import { formkitRenderer } from "@hightouch/lib/templating/formkit-renderer";

import { OVERRIDE_CONFIG_FORM_KEY } from "src/formkit/components/sync-template-lock/constants";
import { JOURNEY_SETTINGS_CONFIG_KEY } from "./utils";

// Keys legitimately stored in a sync config that are never formkit fields.
// They come from the backend or form side channels, not the form definition.
export const SYSTEM_CONFIG_KEYS: ReadonlySet<string> = new Set([
  "configVersion",
  OVERRIDE_CONFIG_FORM_KEY,
  JOURNEY_SETTINGS_CONFIG_KEY,
  "sync_template_overrides",
  // Auto sync all columns toggles written via setValue, not formkit fields.
  "autoSyncColumns",
  "bodyUseAllColumns",
  // Hidden runtime settings some destinations read but never declare in forms.
  // Set manually via the JSON editor, e.g. by support as workarounds.
  "skipWriteTempFile",
  "sqlBatchSize",
  "sqlParallelization",
  // Sync template override metadata persisted alongside the config.
  "overrideConfig",
  // Batch and concurrency knobs read generically by sync frameworks.
  // Some destinations expose them as fields, so a live value can outlast the field.
  "rowsPerBatch",
  "parallelization",
  "batchSize",
  // Legacy s3 object path still honored by the runtime.
  "objectKey",
  // PII hashing opt-out still read by segment destinations at runtime.
  "disablePIIHashing",
  // Rate limiting and retry knobs read generically by sync frameworks.
  "rateLimit",
  "enableSplitBatchRetry",
  // Match Booster target level written by platform machinery, never a form field.
  "htMatchBoosterTargetLevel",
  // Legacy keys that destination migrations still read and upgrade lazily.
  "timestampFrom",
  "userIdFrom",
  "fromId",
]);

// Mapper spread buckets are folded into their base mapping key by the sync SDK.
// Any key with this suffix is live config, e.g. customMappingsSpreadColumns.
const SPREAD_COLUMNS_SUFFIX = "SpreadColumns";

// Props through which a component writes sibling config keys outside its own key.
// NestedRadioGroup writes `${key}List` via listKey and optionally rootKey.
const KEY_WRITING_PROPS = ["listKey", "rootKey", "autoSyncColumnsKey"];

function collectKeyWritingPropValues(
  node: FormkitNode,
  keys: Set<string>,
): void {
  if ("props" in node && node.props) {
    for (const prop of KEY_WRITING_PROPS) {
      const value = node.props[prop];
      if (typeof value === "string") {
        keys.add(value);
      }
    }
  }
  if ("children" in node && Array.isArray(node.children)) {
    for (const child of node.children) {
      collectKeyWritingPropValues(child, keys);
    }
  }
}

// Every config key the form can render, branch on, or write as a side effect.
// Union of component keys, relationship hierarchy keys, and key-writing prop values.
function getKnownConfigKeys(formkitDefinition: FormkitNode): Set<string> {
  const keys = new Set([
    ...getKeysInForm(formkitDefinition, formkitRenderer),
    ...Object.keys(buildRelationshipHierarchy({ node: formkitDefinition })),
  ]);
  collectKeyWritingPropValues(formkitDefinition, keys);
  // Dotted field keys like target.schema store a nested object under the
  // top-level parent key, so that parent is live config too.
  for (const key of [...keys]) {
    const dotIndex = key.indexOf(".");
    if (dotIndex > 0) {
      keys.add(key.slice(0, dotIndex));
    }
  }
  return keys;
}

// Returns top level config keys the form neither renders nor branches on.
// Keys under inactive branches and discriminators like "type" are kept.
export function getOrphanedConfigKeys(
  config: Record<string, unknown> | null | undefined,
  formkitDefinition: FormkitNode | null | undefined,
  systemKeys: ReadonlySet<string> = SYSTEM_CONFIG_KEYS,
): string[] {
  if (!config || !formkitDefinition) {
    return [];
  }

  const knownKeys = getKnownConfigKeys(formkitDefinition);

  return Object.keys(config).filter(
    (key) =>
      !knownKeys.has(key) &&
      !systemKeys.has(key) &&
      !key.endsWith(SPREAD_COLUMNS_SUFFIX),
  );
}
